import { useMemo } from 'react';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { InboxView, ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { ClientSettingsApi } from '@frontend/api-client-settings';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useListDraftsCountQuery } from '@frontend/api-sms-draft';
import { filterLocationIdsByACLs, getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { MultiSettingsHooks } from '@frontend/multi-settings';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useMessagesNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const user = getUser();
  const { getFeatureFlagValue, isFlagStoreLoaded } = useScopedAppFlagStore();

  const actionableTabVisibilityFF = getFeatureFlagValue('nwx:inbox-actionable-tab', 'ANY');

  const { data: actionableTabIsEnabled, isLoading: isEnableActionableTabLoading } =
    MessagingSettingsV1.Queries.useListQuery(
      {
        settingType: SettingType_Enum.INBOX_LOCATION_SETTING,
        orgId: selectedOrgId,
        locationIds: selectedLocationIds,
      },
      {
        enabled: !!actionableTabVisibilityFF,
        select: (data) => {
          return data.settings.some(({ setting }) => {
            if ('inboxLocationSetting' in setting) {
              return setting.inboxLocationSetting.enableActionableTab ?? false;
            }
            return false;
          });
        },
      }
    );

  const isActionableFeatureEnabled = actionableTabIsEnabled && actionableTabVisibilityFF;

  const locationIdsWithInboxAccess = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });

  const { aggregatedValue: autoMessageInboxVisibility, isLoading: isAutoMessageInboxSettingsLoading } =
    MultiSettingsHooks.useBooleanClientSetting({
      set: ClientSettingsApi.querySetKeys.sets.autoMessageInboxVisibility,
      key: ClientSettingsApi.querySetKeys.keys.autoMessageInboxVisibility,
      defaultValue: 'false',
      locationIds: locationIdsWithInboxAccess,
    });

  const avoidLastAutomated = autoMessageInboxVisibility !== true;

  const { data: inboxUnreadCount } = SMSDataV4.Queries.useCountThreadsQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationIds: locationIdsWithInboxAccess,
      filter: {
        inboxView: isActionableFeatureEnabled ? InboxView.ACTIONABLE : InboxView.UNSPECIFIED,
        statuses: isActionableFeatureEnabled ? [] : [ThreadReadStatus.NEW],
        excludeAutomatedLastMessage: isActionableFeatureEnabled ? undefined : avoidLastAutomated,
      },
    },
    options: {
      select: (data) => data.count,
      enabled:
        !!locationIdsWithInboxAccess.length &&
        isFlagStoreLoaded &&
        !isEnableActionableTabLoading &&
        !isAutoMessageInboxSettingsLoading,
    },
  });

  const { data: smsDraftCount } = useListDraftsCountQuery(
    {
      orgId: selectedOrgId,
      userId: user?.userID ?? '',
      locationId: locationIdsWithInboxAccess[0] ?? '',
      groupIds: locationIdsWithInboxAccess,
    },
    { enabled: !!locationIdsWithInboxAccess.length }
  );

  const { data: schedulesCount } = ManualSmsScheduledV1.Queries.useListThreadsCountQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationIds: selectedLocationIds,
    },
  });

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          count: inboxUnreadCount,
          label: t('Inbox'),
          relativePath: '/inbox',
          trackingId: 'nav-messages-inbox',
          description:
            'Central hub for all incoming text messages and outgoing text messages. View, read, and respond to real-time patient and customer communications.',
        },
        {
          count: schedulesCount?.count,
          isPassiveCount: true,
          label: t('Scheduled'),
          relativePath: '/scheduled',
          trackingId: 'nav-messages-scheduled',
          description:
            'Displays all messages that are set for future delivery, with options to edit or cancel before sending.',
        },
        {
          count: smsDraftCount?.count,
          isPassiveCount: true,
          label: t('Drafts'),
          relativePath: '/drafts',
          trackingId: 'nav-messages-drafts',
          description: 'Holds incomplete or unsent messages. Drafts can be edited, scheduled, or sent immediately.',
        },
        {
          label: t('Archived'),
          relativePath: '/archived',
          trackingId: 'nav-messages-archived',
          description:
            'A read-only storage of older messages that have been removed from the active inbox, preserving past communication history.',
        },
        {
          label: t('Blocked'),
          relativePath: '/blocked',
          trackingId: 'nav-messages-blocked',
          description:
            'Lists blocked contacts and message threads, preventing further incoming messages from specific numbers or addresses.',
        },
      ]),
    [t, inboxUnreadCount, schedulesCount?.count, smsDraftCount?.count]
  );

  return useMemo(
    () =>
      createNavItem('messages', {
        hasCount: !!inboxUnreadCount,
        iconName: 'inbox',
        label: t('Messages'),
        root: '/messages',
        subNavItems,
        settings: { path: '/messaging', label: '' },
      }),
    [t, inboxUnreadCount, subNavItems]
  );
};
